import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  ArrowLeft, Printer, FileText, User, Droplets, AlertTriangle,
  Pill, Activity, Scissors, Stethoscope, Building2, CreditCard,
  Phone, Home, Wifi, Dog, DollarSign, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useDemo } from '@/contexts/DemoContext';

/**
 * PrintBinderPage - Generate complete printable life binder PDF
 */

const PrintBinderPage = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { isDemoMode, getProfiles, getMedicalProfile, getAllergies, getMedications, getConditions, getSurgeries, getDoctors, getInsurance, getEmergencyContacts, getDocuments, getHousehold, getPets, getFinancial, getEndOfLife } = useDemo();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, [profileId, isDemoMode]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      // Demo mode: build profile from local demo data
      if (isDemoMode) {
        const profiles = getProfiles() || [];
        const demoProfile = profiles.find(p => p.id === profileId) || profiles[0];
        const medicalProfile = getMedicalProfile() || {};
        const allergies = getAllergies() || [];
        const medications = getMedications() || [];
        const conditions = getConditions() || [];
        const surgeries = getSurgeries() || [];
        const doctors = getDoctors() || [];
        const insurance = getInsurance() || [];
        const emergencyContacts = getEmergencyContacts() || [];
        const documents = getDocuments() || [];
        const household = getHousehold() || {};
        const pets = getPets() || [];
        const financial = getFinancial() || {};
        const endOfLife = getEndOfLife() || {};
        
        const demoFullProfile = {
          name: demoProfile?.name || 'Demo User',
          medical_info: {
            full_name: medicalProfile.full_name || demoProfile?.name,
            date_of_birth: medicalProfile.date_of_birth || '',
            blood_type: medicalProfile.blood_type || '',
            organ_donor: medicalProfile.organ_donor || false,
            allergies: allergies.map(a => ({ name: a.name, severity: a.severity, reaction: a.reaction })),
            medications: medications.map(m => ({ name: m.name, dosage: m.dosage, frequency: m.frequency, reason: m.reason || m.purpose })),
            conditions: conditions.map(c => ({ condition: c.condition || c.name, diagnosed_date: c.diagnosed_date })),
            surgeries: surgeries.map(s => ({ name: s.name || s.procedure, date: s.date, hospital: s.hospital || s.facility })),
            doctors: doctors.map(d => ({ name: d.name, specialty: d.specialty, phone: d.phone, clinic: d.clinic || d.practice })),
            pharmacies: [],
            insurance: insurance.map(i => ({ provider: i.provider, policy_number: i.policy_number, group_number: i.group_number, phone: i.phone }))
          },
          emergency_contacts: emergencyContacts.map(c => ({
            name: c.name,
            relationship: c.relationship,
            phone: c.phone,
            secondary_phone: c.secondary_phone,
            address: c.address
          })),
          documents: documents.map(d => ({ name: d.name || d.title, document_type: d.category || d.document_type })),
          household_info: household,
          pet_info: pets[0] || null,
          financial_accounts: financial.accounts || [],
          // Flatten the nested demo endOfLife structure to match what PrintBinderPage expects
          end_of_life: {
            will_location: endOfLife.will_and_trust?.will_location || endOfLife.will_location || '',
            power_of_attorney: endOfLife.power_of_attorney?.financial_poa 
              ? `Financial: ${endOfLife.power_of_attorney.financial_poa}, Healthcare: ${endOfLife.power_of_attorney.healthcare_proxy}`
              : (typeof endOfLife.power_of_attorney === 'string' ? endOfLife.power_of_attorney : ''),
            funeral_preferences: endOfLife.final_wishes?.michael?.burial_preference 
              ? `${endOfLife.final_wishes.michael.burial_preference} - ${endOfLife.final_wishes.michael.service_preference}`
              : endOfLife.funeral_preferences || '',
            notes: endOfLife.notes || ''
          }
        };
        
        setProfile(demoFullProfile);
        setLoading(false);
        return;
      }
      
      const response = await API.get(`/emergency-binder/profiles/${profileId}`);
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      if (!isDemoMode) {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" />
      </div>
    );
  }

  const medical = profile?.medical_info || {};
  const contacts = profile?.emergency_contacts || [];
  const documents = profile?.documents || [];
  const household = profile?.household_info || {};
  const petInfo = profile?.pet_info;
  const financial = profile?.financial_accounts || [];
  const endOfLife = profile?.end_of_life || {};

  const Section = ({ icon: Icon, title, color, children }) => (
    <div className="mb-8 print:mb-6 print:break-inside-avoid">
      <div className="flex items-center gap-2 mb-4 print:mb-3 border-b-2 pb-2" style={{ borderColor: color }}>
        <Icon className="h-6 w-6 print:h-5 print:w-5" style={{ color }} />
        <h2 className="text-xl print:text-lg font-bold text-slate-800">{title}</h2>
      </div>
      <div className="pl-2">{children}</div>
    </div>
  );

  const InfoRow = ({ label, value }) => value ? (
    <div className="flex py-1 print:py-0.5">
      <span className="text-slate-500 w-40 print:w-32 flex-shrink-0">{label}:</span>
      <span className="font-medium text-slate-800">{value}</span>
    </div>
  ) : null;

  return (
    <div className="min-h-screen bg-slate-50 print:bg-white" data-testid="print-binder-page">
      {/* Header - Hidden on print */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40 print:hidden">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/emergency-binder')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">Print Life Binder</h1>
              <p className="text-xs text-slate-500">{profile?.name}</p>
            </div>
            <Button onClick={handlePrint} className="bg-red-600 hover:bg-red-700">
              <Printer className="h-4 w-4 mr-2" />
              Print Binder
            </Button>
          </div>
        </div>
      </header>

      {/* Printable Content */}
      <main className="max-w-4xl mx-auto px-6 py-8 print:px-8 print:py-4 print:max-w-none">
        
        {/* Cover Page */}
        <div className="text-center mb-12 print:mb-8 print:page-break-after-always">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
            <FileText className="h-10 w-10 text-red-600" />
          </div>
          <h1 className="text-4xl print:text-3xl font-bold text-slate-800 mb-2">
            Emergency Life Binder
          </h1>
          <p className="text-2xl print:text-xl text-slate-600 mb-4">{profile?.name}</p>
          <p className="text-slate-500">
            Generated on {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', month: 'long', day: 'numeric' 
            })}
          </p>
          <div className="mt-8 p-4 bg-red-50 rounded-lg inline-block">
            <p className="text-red-700 font-medium">
              Keep this binder in a safe, accessible location.
            </p>
          </div>
        </div>

        {/* Personal Information */}
        <Section icon={User} title="Personal Information" color="#3B82F6">
          <InfoRow label="Full Name" value={medical.full_name} />
          <InfoRow label="Date of Birth" value={medical.date_of_birth} />
          <InfoRow label="Blood Type" value={medical.blood_type} />
          <InfoRow label="Organ Donor" value={medical.organ_donor ? 'Yes' : 'No'} />
        </Section>

        {/* Allergies */}
        <Section icon={AlertTriangle} title="Allergies" color="#F59E0B">
          {medical.allergies && medical.allergies.length > 0 ? (
            <div className="space-y-2">
              {medical.allergies.map((a) => (
                <div key={a.id || `allergy-${a.name}`} className="p-2 bg-amber-50 rounded print:bg-transparent print:border-b">
                  <span className="font-bold text-amber-800">{a.name}</span>
                  {a.severity && <span className="text-amber-600 ml-2">({a.severity})</span>}
                  {a.reaction && <p className="text-sm text-slate-600">Reaction: {a.reaction}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 italic">No known allergies</p>
          )}
        </Section>

        {/* Medications */}
        <Section icon={Pill} title="Current Medications" color="#3B82F6">
          {medical.medications && medical.medications.length > 0 ? (
            <div className="space-y-2">
              {medical.medications.map((m) => (
                <div key={m.id || `med-${m.name}`} className="p-2 bg-blue-50 rounded print:bg-transparent print:border-b">
                  <span className="font-bold">{m.name}</span>
                  {m.dosage && <span className="text-slate-600 ml-2">{m.dosage}</span>}
                  {m.frequency && <span className="text-slate-600 ml-2">— {m.frequency}</span>}
                  {m.reason && <p className="text-sm text-slate-500">For: {m.reason}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 italic">No current medications</p>
          )}
        </Section>

        {/* Medical Conditions */}
        <Section icon={Activity} title="Medical Conditions" color="#8B5CF6">
          {medical.conditions && medical.conditions.length > 0 ? (
            <ul className="space-y-1">
              {medical.conditions.map((c) => (
                <li key={c.id || `cond-${c.condition}`}>
                  <span className="font-medium">{c.condition}</span>
                  {c.diagnosed_date && <span className="text-slate-500 ml-2">— diagnosed {c.diagnosed_date}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-slate-500 italic">No conditions on file</p>
          )}
        </Section>

        {/* Surgeries */}
        <Section icon={Scissors} title="Surgeries & Procedures" color="#EC4899">
          {medical.surgeries && medical.surgeries.length > 0 ? (
            <ul className="space-y-1">
              {medical.surgeries.map((s) => (
                <li key={s.id || `surgery-${s.name}-${s.date}`}>
                  <span className="font-medium">{s.name}</span>
                  {s.date && <span className="text-slate-500 ml-2">— {s.date}</span>}
                  {s.hospital && <span className="text-slate-500"> at {s.hospital}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-slate-500 italic">No surgeries on file</p>
          )}
        </Section>

        {/* Doctors */}
        <Section icon={Stethoscope} title="Doctors" color="#22C55E">
          {medical.doctors && medical.doctors.length > 0 ? (
            <div className="space-y-2">
              {medical.doctors.map((d) => (
                <div key={d.id || `doc-${d.name}`} className="print:border-b print:pb-2">
                  <p className="font-medium">{d.name} {d.specialty && `(${d.specialty})`}</p>
                  {d.phone && <p className="text-sm text-slate-600">Phone: {d.phone}</p>}
                  {d.clinic && <p className="text-sm text-slate-600">Clinic: {d.clinic}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 italic">No doctors on file</p>
          )}
        </Section>

        {/* Pharmacy */}
        <Section icon={Building2} title="Pharmacy" color="#06B6D4">
          {medical.pharmacies && medical.pharmacies.length > 0 ? (
            <div className="space-y-2">
              {medical.pharmacies.map((p) => (
                <div key={p.id || `pharm-${p.name}`}>
                  <p className="font-medium">{p.name}</p>
                  {p.phone && <p className="text-sm text-slate-600">Phone: {p.phone}</p>}
                  {p.address && <p className="text-sm text-slate-600">Address: {p.address}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 italic">No pharmacy on file</p>
          )}
        </Section>

        {/* Insurance */}
        <Section icon={CreditCard} title="Insurance" color="#6366F1">
          {medical.insurance && medical.insurance.length > 0 ? (
            <div className="space-y-2">
              {medical.insurance.map((ins) => (
                <div key={ins.id || `ins-${ins.provider}-${ins.policy_number}`} className="print:border-b print:pb-2">
                  <p className="font-medium">{ins.provider}</p>
                  {ins.policy_number && <p className="text-sm text-slate-600">Policy #: {ins.policy_number}</p>}
                  {ins.group_number && <p className="text-sm text-slate-600">Group #: {ins.group_number}</p>}
                  {ins.phone && <p className="text-sm text-slate-600">Phone: {ins.phone}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 italic">No insurance on file</p>
          )}
        </Section>

        {/* Emergency Contacts */}
        <Section icon={Phone} title="Emergency Contacts" color="#8B5CF6">
          {contacts.length > 0 ? (
            <div className="space-y-3">
              {contacts.map((c) => (
                <div key={c.id || `contact-${c.name}-${c.phone}`} className="p-3 bg-purple-50 rounded print:bg-transparent print:border-b">
                  <p className="font-bold text-lg">{c.name}</p>
                  {c.relationship && <p className="text-slate-600">{c.relationship}</p>}
                  {c.phone && <p className="text-slate-800 font-medium">Phone: {c.phone}</p>}
                  {c.secondary_phone && <p className="text-slate-600">Alt: {c.secondary_phone}</p>}
                  {c.address && <p className="text-sm text-slate-500">{c.address}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 italic">No emergency contacts on file</p>
          )}
        </Section>

        {/* Important Documents */}
        <Section icon={FileText} title="Important Documents" color="#3B82F6">
          {documents.length > 0 ? (
            <ul className="space-y-1">
              {documents.map((d, i) => (
                <li key={i} className="flex justify-between">
                  <span className="font-medium">{d.name}</span>
                  {d.document_type && <span className="text-slate-500">{d.document_type}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-slate-500 italic">No documents on file</p>
          )}
        </Section>

        {/* Household Information */}
        <Section icon={Home} title="Household Information" color="#22C55E">
          <InfoRow label="Home Address" value={household.home_address} />
          <InfoRow label="Garage Code" value={household.garage_code} />
          <InfoRow label="Alarm Code" value={household.alarm_code} />
          <InfoRow label="Gate Code" value={household.gate_code} />
        </Section>

        {/* WiFi */}
        {(household.wifi_network || household.wifi_password) && (
          <Section icon={Wifi} title="WiFi Information" color="#0EA5E9">
            <InfoRow label="Network" value={household.wifi_network} />
            <InfoRow label="Password" value={household.wifi_password} />
          </Section>
        )}

        {/* Pet Information */}
        {petInfo && petInfo.name && (
          <Section icon={Dog} title="Pet Emergency Information" color="#F97316">
            <InfoRow label="Name" value={petInfo.name} />
            <InfoRow label="Breed" value={petInfo.breed} />
            <InfoRow label="Veterinarian" value={petInfo.veterinarian} />
            <InfoRow label="Vet Phone" value={petInfo.vet_phone} />
            <InfoRow label="Medications" value={petInfo.medications} />
            {petInfo.special_instructions && (
              <div className="mt-2">
                <p className="text-slate-500">Special Instructions:</p>
                <p className="text-slate-800">{petInfo.special_instructions}</p>
              </div>
            )}
          </Section>
        )}

        {/* Financial Overview */}
        {financial.length > 0 && (
          <Section icon={DollarSign} title="Financial Overview" color="#F59E0B">
            <div className="space-y-2">
              {financial.map((f, i) => (
                <div key={i}>
                  <p className="font-medium">{f.bank_name} {f.account_type && `(${f.account_type})`}</p>
                  {f.notes && <p className="text-sm text-slate-500">{f.notes}</p>}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* End of Life */}
        {(endOfLife.will_location || endOfLife.power_of_attorney || endOfLife.funeral_preferences) && (
          <Section icon={BookOpen} title="End-of-Life Planning" color="#6B7280">
            <InfoRow label="Will Location" value={endOfLife.will_location} />
            <InfoRow label="Power of Attorney" value={endOfLife.power_of_attorney} />
            {endOfLife.funeral_preferences && (
              <div className="mt-2">
                <p className="text-slate-500">Funeral Preferences:</p>
                <p className="text-slate-800">{endOfLife.funeral_preferences}</p>
              </div>
            )}
            {endOfLife.notes && (
              <div className="mt-2">
                <p className="text-slate-500">Notes:</p>
                <p className="text-slate-800">{endOfLife.notes}</p>
              </div>
            )}
          </Section>
        )}

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t print:mt-8 print:pt-4">
          <p className="text-slate-500 text-sm">
            This Life Binder was generated using the Forget Me Not app.
          </p>
          <p className="text-slate-400 text-xs mt-1">
            Please keep this document updated and store in a safe, accessible location.
          </p>
        </div>
      </main>

      {/* Print Button - Hidden on print */}
      <div className="fixed bottom-6 left-0 right-0 flex justify-center print:hidden">
        <Button
          onClick={handlePrint}
          size="lg"
          className="bg-red-600 hover:bg-red-700 shadow-lg"
        >
          <Printer className="h-5 w-5 mr-2" />
          Print Complete Binder
        </Button>
      </div>
    </div>
  );
};

export default PrintBinderPage;
